/**
 * Document type → expected field mappings.
 * From REGULATORY_SEED_DATA.md §7.
 */

export interface DocTypeMapping {
  description: string;
  country_variants: Record<string, string>;
  expected_fields: string[];
  is_also_submission: boolean;
}

export const DOC_TYPE_FIELD_MAP: Record<string, DocTypeMapping> = {
  incorporation_cert: {
    description: "Company registration certificate from national registry",
    country_variants: {
      KE: "Certificate of Incorporation + CR12",
      NG: "CAC Certificate of Incorporation",
      ZA: "CIPC Registration Certificate",
      GH: "ORC Registration Certificate",
      NA: "BIPA Registration Certificate",
      ZM: "PACRA Registration Certificate",
    },
    expected_fields: ["C-001", "C-002", "C-003", "C-004"],
    is_also_submission: true,
  },
  tax_clearance: {
    description: "Tax compliance certificate from revenue authority",
    country_variants: {
      KE: "KRA Tax Compliance Certificate",
      NG: "FIRS Tax Clearance Certificate",
      ZA: "SARS Tax Clearance PIN",
      GH: "GRA Tax Clearance Certificate",
      NA: "NamRA Tax Compliance Certificate",
      ZM: "ZRA Tax Clearance Certificate",
    },
    expected_fields: ["C-005", "C-006"],
    is_also_submission: true,
  },
  bank_offer_letter: {
    description: "Financing commitment from lender or DFI",
    country_variants: { KE: "Bank Offer Letter", NG: "Bank Offer Letter", ZA: "Facility Letter / Term Sheet", GH: "Bank Offer Letter", NA: "Bank Offer Letter", ZM: "Bank Offer Letter" },
    expected_fields: ["F-011", "F-012", "F-014"],
    is_also_submission: true,
  },
  tariff_confirmation: {
    description: "Regulator tariff approval or auction result",
    country_variants: {
      KE: "EPRA Tariff Approval Letter / Auction Result",
      NG: "NERC Tariff Order / AFUR Tool Output",
      ZA: "REIPPPP Preferred Bidder Confirmation / Wheeling Rate Agreement",
      GH: "PURC Tariff Approval / Feed-in Tariff",
      NA: "ECB Tariff Determination",
      ZM: "ERB Tariff Approval / GET FiT Terms",
    },
    expected_fields: ["F-006", "F-007", "F-008", "F-009"],
    is_also_submission: false,
  },
  land_title: {
    description: "Proof of land ownership or leasehold rights",
    country_variants: {
      KE: "Title Deed / Allotment Letter / Community Land Certificate",
      NG: "Certificate of Occupancy (C of O)",
      ZA: "Title Deed",
      GH: "Lands Commission Certificate / Stool Land Lease",
      NA: "Title Deed / Communal Land Board Consent",
      ZM: "Commissioner of Lands Leasehold / Chief's Consent",
    },
    expected_fields: ["S-009", "S-010", "S-011", "S-012", "S-005"],
    is_also_submission: true,
  },
  lease_agreement: {
    description: "Land lease agreement between developer and landowner",
    country_variants: { KE: "Lease Agreement", NG: "Deed of Assignment / Lease", ZA: "Lease Agreement", GH: "Lease Agreement / Stool Land Concession", NA: "Lease Agreement", ZM: "Lease Agreement" },
    expected_fields: ["S-012", "S-013", "S-014", "S-015"],
    is_also_submission: true,
  },
  solar_resource_assessment: {
    description: "Solar yield study from resource consultant",
    country_variants: { KE: "Solar Resource Assessment", NG: "Solar Resource Assessment", ZA: "Solar Resource Assessment", GH: "Solar Resource Assessment", NA: "Solar Resource Assessment", ZM: "Solar Resource Assessment" },
    expected_fields: ["P-016", "P-017", "P-018", "P-019"],
    is_also_submission: false,
  },
  grid_impact_study: {
    description: "Grid study report from utility/system operator",
    country_variants: {
      KE: "KPLC/KETRACO Grid Impact Study Report",
      NG: "TCN Evacuation Study Report",
      ZA: "Eskom Cost Estimate Letter (CEL)",
      GH: "GRIDCo Grid Impact Study",
      NA: "NamPower Grid Study",
      ZM: "ZESCO Grid Study Report",
    },
    expected_fields: ["G-003", "G-005", "G-006", "P-022"],
    is_also_submission: false,
  },
  epc_proposal: {
    description: "Engineering procurement construction proposal or contract",
    country_variants: { KE: "EPC Proposal / Contract", NG: "EPC Proposal / Contract", ZA: "EPC Proposal / Contract", GH: "EPC Proposal / Contract", NA: "EPC Proposal / Contract", ZM: "EPC Proposal / Contract" },
    expected_fields: ["F-001", "F-003", "P-004", "P-005", "P-006", "P-009", "P-010"],
    is_also_submission: false,
  },
  pv_module_datasheet: {
    description: "PV module manufacturer technical specification sheet",
    country_variants: { KE: "PV Module Datasheet", NG: "PV Module Datasheet", ZA: "PV Module Datasheet", GH: "PV Module Datasheet", NA: "PV Module Datasheet", ZM: "PV Module Datasheet" },
    expected_fields: ["P-005", "P-006", "P-007", "P-008", "P-020"],
    is_also_submission: false,
  },
  inverter_datasheet: {
    description: "Inverter manufacturer technical specification sheet",
    country_variants: { KE: "Inverter Datasheet", NG: "Inverter Datasheet", ZA: "Inverter Datasheet", GH: "Inverter Datasheet", NA: "Inverter Datasheet", ZM: "Inverter Datasheet" },
    expected_fields: ["P-009", "P-010", "P-011"],
    is_also_submission: false,
  },
  eia_certificate: {
    description: "Environmental clearance issued by environmental authority",
    country_variants: {
      KE: "NEMA EIA License",
      NG: "FMoE EIA Approval Certificate",
      ZA: "DFFE Environmental Authorisation / Exclusion Norm Registration",
      GH: "EPA Environmental Permit",
      NA: "ECC Environmental Clearance",
      ZM: "ZEMA EIA Certificate",
    },
    expected_fields: ["E-005", "E-006"],
    is_also_submission: true,
  },
  ppa_agreement: {
    description: "Signed Power Purchase Agreement",
    country_variants: {
      KE: "KPLC PPA / Corporate PPA",
      NG: "NBET PPA / Bilateral PPA / DisCo PPA",
      ZA: "Eskom PPA (REIPPPP) / Corporate PPA / Wheeling PPA",
      GH: "ECG PPA / GRIDCo PPA",
      NA: "NamPower PPA",
      ZM: "ZESCO PPA / GET FiT PPA",
    },
    expected_fields: ["F-006", "F-007", "F-008", "F-009", "O-001", "O-004"],
    is_also_submission: true,
  },
  grid_connection_agreement: {
    description: "Signed grid connection and use-of-system agreement",
    country_variants: {
      KE: "KPLC Grid Connection Agreement",
      NG: "TCN Grid Connection Agreement",
      ZA: "Eskom CUSA/CUOSA",
      GH: "GRIDCo Grid Connection Agreement",
      NA: "NamPower Connection Agreement",
      ZM: "ZESCO Grid Connection Agreement",
    },
    expected_fields: ["G-003", "G-006", "G-007", "G-008", "P-022"],
    is_also_submission: true,
  },
  community_assembly_minutes: {
    description: "Record of community consent for land use",
    country_variants: { KE: "Community Assembly Minutes", NG: "Community Engagement Record", ZA: "Community Participation Record", GH: "Community Engagement Record", NA: "Community Assembly Minutes", ZM: "Community Engagement Record" },
    expected_fields: ["CS-002", "CS-003"],
    is_also_submission: true,
  },
  bee_certificate: {
    description: "B-BBEE verification certificate (South Africa only)",
    country_variants: { ZA: "B-BBEE Certificate" },
    expected_fields: ["C-012"],
    is_also_submission: true,
  },
  generation_license: {
    description: "Issued generation license or registration certificate",
    country_variants: {
      KE: "EPRA Generation License",
      NG: "NERC Generation License",
      ZA: "NERSA Registration Certificate",
      GH: "Energy Commission Generation License",
      NA: "ECB Generation Licence",
      ZM: "ERB Generation Licence",
    },
    expected_fields: ["R-001", "R-002", "R-003"],
    is_also_submission: true,
  },
};

/** Get all doc type keys */
export const DOC_TYPES = Object.keys(DOC_TYPE_FIELD_MAP);

/** Get the display name for a doc type in a given country */
export function getDocTypeDisplayName(docType: string, countryCode: string): string {
  const mapping = DOC_TYPE_FIELD_MAP[docType];
  if (!mapping) return docType.replace(/_/g, " ");
  return mapping.country_variants[countryCode] || mapping.description;
}
